(() => {
  if (window.CsbInteractions) {
    window.CsbInteractions.init(document);
    return;
  }

  const accordionSelector = '.csb-faq__questions details, .csb-product-answer';
  const specSelector = '.csb-product-specs > div, .csb-spec-reveal';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const accordions = new Map();
  const revealedSpecs = new WeakSet();
  const specAnimations = new Map();
  const observedSpecs = new Set();
  const supportsAnimation = typeof Element.prototype.animate === 'function';
  let answerId = 0;

  const elementsWithin = (root, selector) => [
    ...(root.matches?.(selector) ? [root] : []),
    ...root.querySelectorAll(selector)
  ];

  class Accordion {
    constructor(details) {
      this.details = details;
      this.summary = details.querySelector(':scope > summary');
      this.content = details.querySelector(':scope > .csb-faq__answer, :scope > .csb-answer__content');
      if (!this.summary || !this.content) return;

      this.target = details.open;
      this.openAttribute = details.open;
      this.previousExpanded = this.summary.getAttribute('aria-expanded');
      this.previousControls = this.summary.getAttribute('aria-controls');
      this.generatedId = !this.content.id;
      if (this.generatedId) this.content.id = `CsbAnswerMotion-${++answerId}`;
      this.summary.setAttribute('aria-controls', this.content.id);
      details.dataset.csbAccordion = '';
      details.dataset.csbExpanded = String(this.target);
      this.syncExpanded();

      this.onClick = (event) => {
        if (event.defaultPrevented || event.target.closest('a, button, input, select, textarea')) return;
        event.preventDefault();
        this.transition(!this.target);
      };
      this.onToggle = () => {
        if (details.open !== this.openAttribute) this.finish(details.open);
        else this.syncExpanded();
      };
      this.onResize = () => {
        if (!this.heightAnimation || this.resizeFrame) return;
        this.resizeFrame = requestAnimationFrame(() => {
          this.resizeFrame = 0;
          if (this.heightAnimation && Math.abs(this.destination - this.measure(this.target)) > 1) {
            this.transition(this.target);
          }
        });
      };

      this.summary.addEventListener('click', this.onClick);
      details.addEventListener('toggle', this.onToggle);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.summary);
        this.resizeObserver.observe(this.content);
      } else {
        window.addEventListener('resize', this.onResize, { passive: true });
      }
      this.ready = true;
    }

    syncExpanded() {
      this.summary.setAttribute('aria-expanded', String(this.details.open));
    }

    setOpen(open) {
      this.openAttribute = open;
      this.details.open = open;
      this.syncExpanded();
    }

    measure(open) {
      const style = getComputedStyle(this.details);
      const edges = ['borderTopWidth', 'borderBottomWidth', 'paddingTop', 'paddingBottom']
        .reduce((total, property) => total + (parseFloat(style[property]) || 0), 0);
      return this.summary.getBoundingClientRect().height + edges + (open ? this.content.offsetHeight : 0);
    }

    cancelAnimations() {
      if (this.heightAnimation) {
        this.heightAnimation.onfinish = null;
        this.heightAnimation.cancel();
        this.heightAnimation = null;
      }
      this.contentAnimation?.cancel();
      this.contentAnimation = null;
    }

    transition(open) {
      const currentHeight = this.details.getBoundingClientRect().height;
      const wasOpen = this.details.open;
      const contentStyle = getComputedStyle(this.content);
      const opacity = wasOpen ? contentStyle.opacity : '0';
      const transform = wasOpen ? contentStyle.transform : 'translateY(-7px)';
      this.cancelAnimations();
      this.target = open;
      this.details.dataset.csbExpanded = String(open);

      if (reducedMotion.matches || !supportsAnimation) {
        this.finish(open);
        return;
      }

      this.details.style.height = `${currentHeight}px`;
      this.details.style.overflow = 'hidden';
      this.setOpen(true);
      this.destination = this.measure(open);
      const distance = Math.abs(this.destination - currentHeight);
      if (distance < 1) {
        this.finish(open);
        return;
      }

      const duration = Math.min(560, Math.max(220, 240 + distance * 0.28));
      const timing = { duration, easing: 'cubic-bezier(.22, 1, .36, 1)', fill: 'both' };
      const animation = this.details.animate([
        { height: `${currentHeight}px` },
        { height: `${this.destination}px` }
      ], timing);
      this.heightAnimation = animation;
      this.contentAnimation = this.content.animate([
        { opacity, transform },
        { opacity: open ? 1 : 0, transform: open ? 'translateY(0)' : 'translateY(-7px)' }
      ], timing);
      animation.onfinish = () => {
        if (this.heightAnimation === animation) this.finish(open);
      };
    }

    finish(open = this.target) {
      this.cancelAnimations();
      this.target = open;
      this.details.dataset.csbExpanded = String(open);
      this.setOpen(open);
      this.details.style.removeProperty('height');
      this.details.style.removeProperty('overflow');
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = 0;
    }

    destroy() {
      this.finish();
      this.summary.removeEventListener('click', this.onClick);
      this.details.removeEventListener('toggle', this.onToggle);
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.onResize);
      if (this.previousExpanded === null) this.summary.removeAttribute('aria-expanded');
      else this.summary.setAttribute('aria-expanded', this.previousExpanded);
      if (this.previousControls === null) this.summary.removeAttribute('aria-controls');
      else this.summary.setAttribute('aria-controls', this.previousControls);
      if (this.generatedId) this.content.removeAttribute('id');
      delete this.details.dataset.csbAccordion;
      delete this.details.dataset.csbExpanded;
    }
  }

  const revealSpec = (element, delay = 0) => {
    if (revealedSpecs.has(element)) return;
    revealedSpecs.add(element);
    observedSpecs.delete(element);
    if (reducedMotion.matches || !supportsAnimation) return;
    const animation = element.animate([
      { opacity: 0.15, transform: 'translateY(24px)' },
      { opacity: 1, transform: 'translateY(0)' }
    ], { duration: 850, delay, easing: 'cubic-bezier(.2, .8, .2, 1)', fill: 'backwards' });
    specAnimations.set(element, animation);
    animation.onfinish = () => specAnimations.delete(element);
  };

  const specObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    let order = 0;
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      specObserver.unobserve(entry.target);
      revealSpec(entry.target, Math.min(order++ * 95, 285));
    });
  }, { threshold: 0.2, rootMargin: '0px 0px -5% 0px' }) : null;

  const init = (root = document) => {
    accordions.forEach((controller, element) => {
      if (!element.isConnected) {
        controller.destroy();
        accordions.delete(element);
      }
    });
    elementsWithin(root, accordionSelector).forEach((element) => {
      if (accordions.has(element)) return;
      const controller = new Accordion(element);
      if (controller.ready) accordions.set(element, controller);
    });
    elementsWithin(root, specSelector).forEach((element) => {
      if (revealedSpecs.has(element) || observedSpecs.has(element)) return;
      if (!specObserver) return;
      observedSpecs.add(element);
      specObserver.observe(element);
    });
  };

  const dispose = (root) => {
    accordions.forEach((controller, element) => {
      if (element === root || root.contains(element)) {
        controller.destroy();
        accordions.delete(element);
      }
    });
    observedSpecs.forEach((element) => {
      if (element === root || root.contains(element)) {
        specObserver?.unobserve(element);
        observedSpecs.delete(element);
      }
    });
    specAnimations.forEach((animation, element) => {
      if (element === root || root.contains(element)) {
        animation.cancel();
        specAnimations.delete(element);
      }
    });
  };

  const updateMotion = () => {
    if (!reducedMotion.matches) return;
    accordions.forEach((controller) => controller.finish());
    specAnimations.forEach((animation) => animation.cancel());
    specAnimations.clear();
  };

  if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', updateMotion);
  else reducedMotion.addListener(updateMotion);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => dispose(event.target));
  window.CsbInteractions = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
